<template>
  <div class="project-architecture" :class="{ 'is-inline': inline, 'is-contextual': contextual }" :data-architecture-project="projectId" @click.stop @keydown.stop>
    <figure v-if="inline" class="architecture-inline">
      <button type="button" class="architecture-inline-art image-enlarge" :aria-label="`Enlarge ${artwork.title} ${diagramKind}`" aria-haspopup="dialog" @click="openViewer">
        <img :src="artwork.src" :width="artwork.width" :height="artwork.height" :alt="artwork.alt" loading="lazy" decoding="async">
      </button>
      <figcaption v-if="!contextual || showControls">
        <span v-if="!contextual">{{ artwork.kind === 'Workflow' ? 'Presentation workflow' : 'System architecture' }}</span>
        <button v-if="showControls" type="button" class="architecture-expand" :aria-label="`Enlarge ${artwork.title} ${diagramKind}`" aria-haspopup="dialog" @click="openViewer">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M12 3h5v5M17 3l-5.5 5.5M8 17H3v-5m0 5 5.5-5.5" /></svg>
        </button>
      </figcaption>
    </figure>
    <button v-else type="button" class="architecture-entry" :class="[appearance === 'inline' ? 'mono-link mono-workflow-link' : 'resource-action', { 'workflow-entry': artwork.kind === 'Workflow' }]" :aria-label="`View ${artwork.title} ${diagramKind}`" aria-haspopup="dialog" @click="openViewer">
      <span>{{ artwork.kind }}</span>
      <WorkflowDrawingIcon v-if="artwork.kind === 'Workflow'" />
      <svg v-else viewBox="0 0 20 20" aria-hidden="true"><rect x="7" y="2.5" width="6" height="4" rx=".8" /><rect x="1.5" y="13.5" width="6" height="4" rx=".8" /><rect x="12.5" y="13.5" width="6" height="4" rx=".8" /><path d="M10 6.5v3.5m-5.5 3.5V10h11v3.5" /></svg>
    </button>
    <Teleport to="body">
      <dialog ref="viewer" class="architecture-viewer" :aria-label="`${artwork.title} ${diagramKind}`" @close="onClose" @click.stop="closeOnBackdrop" @keydown.stop>
        <header class="architecture-viewer-head">
          <span>{{ artwork.title }}<small>{{ artwork.kind }}</small></span>
          <button type="button" :aria-label="`Close ${diagramKind}`" autofocus @click="closeViewer"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg></button>
        </header>
        <div class="architecture-viewer-body">
          <img v-if="viewerOpen" :src="artwork.src" :width="artwork.width" :height="artwork.height" :alt="artwork.alt">
        </div>
      </dialog>
    </Teleport>
  </div>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { projectArchitectures } from '../../data/projectArchitectures';
import '../../assets/css/resource-action.css';
import WorkflowDrawingIcon from './WorkflowDrawingIcon.vue';

export default {
  name: 'ProjectArchitecture',
  components: { WorkflowDrawingIcon },
  props: {
    appearance: { type: String, default: 'button' },
    projectId: { type: String, required: true },
    inline: { type: Boolean, default: false },
    contextual: { type: Boolean, default: false },
    showControls: { type: Boolean, default: true },
  },
  data() { return { viewerOpen: false }; },
  computed: {
    artwork() { return projectArchitectures[this.projectId]; },
    diagramKind() { return this.artwork.kind.toLowerCase(); },
  },
  beforeUnmount() { if (this.viewerOpen) this.onClose(); },
  methods: {
    openViewer() {
      this._trigger = document.activeElement;

      this.viewerOpen = true;
      this._releaseScroll ||= lockBodyScroll();
      this.$refs.viewer.showModal();
    },
    closeViewer() { this.$refs.viewer.close(); },
    onClose() {
      if (!this.viewerOpen) return;
      this.viewerOpen = false;
      this._releaseScroll?.();this._releaseScroll=null;
      if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
    },
    closeOnBackdrop(event) { if (event.target === this.$refs.viewer) this.closeViewer(); },
  },
};
</script>

<style>
.project-architecture { display: inline-block; max-width: 100%; vertical-align: middle; }
.project-architecture.is-inline { display: block; margin: 2rem 0 2.4rem; }
.project-architecture.is-contextual .architecture-inline figcaption { justify-content: flex-end; }
.project-architecture svg, .architecture-viewer svg { display: block; width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.25; stroke-linecap: round; stroke-linejoin: round; }
.architecture-entry svg { opacity: .7; }
.mono-home .mono-workflow-link { border: 0; border-radius: 0; background: none; color: inherit; font-family: inherit; cursor: var(--site-interactive-cursor); }
.mono-workflow-link:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.project-architecture .workflow-drawing-icon, .mono-home .mono-workflow-link > .workflow-drawing-icon { flex: 0 0 26px; width: 26px; height: 26px; margin-block: -5px; stroke-width: 1.65; opacity: 1; transform: none; transition: none; }
.architecture-inline { width: min(100%, calc((100svh - 160px) * 1600 / 1180)); margin: 0 auto; }
.architecture-inline-art { display: block; width: 100%; padding: 0; border: 0; border-radius: 0; background: #eeebe4; cursor: var(--site-zoom-in-cursor); }
.project-architecture .architecture-inline-art img { display: block; width: 100%; height: auto; max-width: none; margin: 0; border: 0; border-radius: 0; }
.architecture-inline figcaption { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-top: 6px; color: var(--site-text-secondary); font: 12px/1.5 'Quattrocento Sans', sans-serif; }
.architecture-expand { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0; border: 0; background: none; color: inherit; font: inherit; cursor: var(--site-interactive-cursor); transition: color 160ms ease; }
.architecture-expand svg { width: 16px; height: 16px; }
.architecture-inline button:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.architecture-viewer { box-sizing: border-box; position: fixed; inset: 0; width: calc(100vw - 32px); height: calc(100dvh - 32px); max-width: 1900px; max-height: none; padding: 0; border: 0; background: #f5f3ee; color: #33332e; overflow: hidden; }
.architecture-viewer[open] { display: flex; flex-direction: column; }
.architecture-viewer::backdrop { background: rgb(20 20 18 / 75%); }
.architecture-viewer-head { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; padding: 8px 12px 8px 20px; font: 13px/1.5 'Quattrocento Sans', sans-serif; }
.architecture-viewer-head small { margin-left: 16px; opacity: .65; font: inherit; }
.architecture-viewer-head button { display: grid; place-items: center; width: 44px; height: 44px; border: 0; background: none; color: inherit; cursor: var(--site-interactive-cursor); }
.architecture-viewer-head button:focus-visible { outline: 2px solid currentColor; outline-offset: -3px; }
.architecture-viewer-body { display: grid; place-items: center; flex: 1; min-height: 0; padding: 0 16px 16px; }
.architecture-viewer-body img { display: block; width: 100%; height: 100%; min-height: 0; max-height: 100%; margin: 0; border-radius: 0; object-fit: contain; }
body.dark-mode .architecture-viewer { background: #242421; color: #e1e0da; }
.architecture-entry:active, .architecture-expand:active { transform: scale(.98); }
@media (hover: hover) and (pointer: fine) {
  .architecture-expand:hover { color: var(--site-text-primary); }
  .architecture-viewer-head button:hover { background: rgb(125 125 115 / 10%); }
}
@media (max-width: 720px) {
  .architecture-viewer { width: 100vw; height: 100dvh; }
  .architecture-viewer-head { padding: 8px 8px 8px 16px; }
  .architecture-viewer-head small { display: block; margin: 0; font-size: 11px; }
  .architecture-viewer-body { padding: 0 8px 8px; }
}
@media (prefers-reduced-motion: reduce) { .project-architecture *, .architecture-viewer * { transition: none; } }
</style>
